import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { CampaignList } from '@/modules/lists/types/lists.types';
import { useGetSegments } from '@/modules/segments/hooks/use-segments';
import { SegmentBuilderDrawer } from '@/modules/segments/components/segment-builder-drawer';
import {
  getAudienceOverlapIds,
  isAudienceSelectionValid,
  toggleAudienceId,
  type CampaignAudienceSelection,
} from '../../utils/campaign-audience.utils';
import { useAudienceKpiCounts } from '../../hooks/use-audience-kpi-counts';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { useToast } from '@/hooks/use-toast';

type CampaignAudienceStepProps = {
  selection: CampaignAudienceSelection;
  onChange: (next: CampaignAudienceSelection) => void;
  lists: CampaignList[];
  isListsLoading?: boolean;
  isListsError?: boolean;
  /** Bound campaign — used so KPIs come from server estimate (unique contacts). */
  campaignId?: string;
  onAudienceCounts?: (counts: { audience: number; sendable: number }) => void;
  isPublishAudienceResolved?: boolean;
  /** TEMP: hide blocking validation copy while reviewing design. */
  designPreview?: boolean;
};

const formatMembers = (count: number): string =>
  `${count.toLocaleString()} ${count === 1 ? 'member' : 'members'}`;

const CheckRow = ({
  checked,
  label,
  sub,
  disabled,
  onToggle,
}: {
  checked: boolean;
  label: string;
  sub?: string;
  disabled?: boolean;
  onToggle: () => void;
}) => (
  <label
    className={`flex items-start gap-2 rounded-md px-2.5 py-2 text-sm text-foreground ${
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/50'
    }`}
  >
    <Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={() => onToggle()}
      className="mt-0.5 border-[#CECECE] data-[state=checked]:border-primary-600 data-[state=checked]:bg-primary-600"
    />
    <span className="min-w-0">
      <span className="block">{label}</span>
      {sub ? <span className="block text-xs text-muted-foreground">{sub}</span> : null}
    </span>
  </label>
);

const KpiTile = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="rounded-lg border border-border bg-background px-3 py-2.5">
    <span className="block text-xs text-muted-foreground">{label}</span>
    <span className="mt-0.5 block text-lg font-semibold tabular-nums text-foreground">{value}</span>
  </div>
);

const formatCount = (value: number) => value.toLocaleString();

const SKIP_REASON_LABELS = [
  { key: 'unsubscribed', label: 'Unsubscribed' },
  { key: 'pending', label: 'Pending' },
  { key: 'suppressed', label: 'Suppressed' },
  { key: 'noAddress', label: 'No address' },
  { key: 'invalidEmail', label: 'Invalid email' },
  { key: 'noSubscriptionStatus', label: 'No subscription status' },
] as const;

export const CampaignAudienceStep = ({
  selection,
  onChange,
  lists,
  isListsLoading,
  isListsError,
  campaignId,
  onAudienceCounts,
  isPublishAudienceResolved = true,
  designPreview = false,
}: CampaignAudienceStepProps) => {
  const { toast } = useToast();
  const [newSegmentOpen, setNewSegmentOpen] = useState(false);
  const selectionValid = isAudienceSelectionValid(selection);
  const {
    data: segmentsData,
    isLoading: isSegmentsLoading,
    isError: isSegmentsError,
    refetch: refetchSegments,
  } = useGetSegments({ pageNo: 1, pageSize: 100, sort: 'updated', order: 'desc' });
  // Empty lists stay visible (disabled) so marketers can see them and why they can't be picked.
  const selectableLists = lists;
  const selectableSegments = segmentsData?.items ?? [];
  // A just-created segment is not in the cached list until the refetch lands; keep it selected.
  const pendingSegmentIdsRef = useRef<Set<string>>(new Set());
  const resolvedIncludeSegmentIds = useMemo(() => {
    if (!segmentsData) return selection.includeSegmentIds;
    const known = new Set(segmentsData.items.map((segment) => segment.itemId));
    pendingSegmentIdsRef.current.forEach((id) => {
      if (known.has(id)) pendingSegmentIdsRef.current.delete(id);
    });
    return selection.includeSegmentIds.filter(
      (id) => known.has(id) || pendingSegmentIdsRef.current.has(id)
    );
  }, [segmentsData, selection.includeSegmentIds]);
  const selectionForCounts = {
    includeListIds: selection.includeListIds,
    includeSegmentIds: resolvedIncludeSegmentIds,
    excludeListIds: selection.excludeListIds ?? [],
    excludeSegmentIds: selection.excludeSegmentIds,
  };
  const {
    data: kpiData,
    isLoading: isKpiLoading,
    isError: isKpiError,
    isFetching: isKpiFetching,
  } = useAudienceKpiCounts(selectionForCounts, {
    enabled: selectionValid,
    campaignId,
  });

  const audienceCount = selectionValid && !isKpiError ? (kpiData?.audience ?? 0) : 0;
  const sendableCount = selectionValid && !isKpiError ? (kpiData?.sendable ?? 0) : 0;
  const skippedCount =
    selectionValid && !isKpiError
      ? (kpiData?.skipped ?? Math.max(0, audienceCount - sendableCount))
      : 0;
  const listsAndSegmentsCount =
    selection.includeListIds.length + selection.includeSegmentIds.length;
  const countsLoading = selectionValid && (isKpiLoading || isKpiFetching);

  useEffect(() => {
    onAudienceCounts?.({ audience: audienceCount, sendable: sendableCount });
  }, [audienceCount, sendableCount, onAudienceCounts]);

  useEffect(() => {
    if (isListsLoading || isListsError || lists.length === 0) return;

    const allowedListIds = new Set(
      lists.filter((list) => (list.memberCount ?? 0) > 0).map((list) => list.id)
    );
    // Only drop selected lists that are now empty or gone.
    const nextListIds = selection.includeListIds.filter((id) => allowedListIds.has(id));
    if (nextListIds.length === selection.includeListIds.length) return;

    onChange({ ...selection, includeListIds: nextListIds });
  }, [isListsError, isListsLoading, lists, onChange, selection]);

  useEffect(() => {
    if (isSegmentsLoading || isSegmentsError || !segmentsData) return;

    const nextSegmentIds = resolvedIncludeSegmentIds;
    if (
      nextSegmentIds.length === selection.includeSegmentIds.length &&
      nextSegmentIds.every((id, index) => id === selection.includeSegmentIds[index])
    ) {
      return;
    }

    onChange({ ...selection, includeSegmentIds: nextSegmentIds });
  }, [
    isSegmentsError,
    isSegmentsLoading,
    onChange,
    resolvedIncludeSegmentIds,
    segmentsData,
    selection,
  ]);

  const setIncludeList = (id: string) =>
    onChange({
      ...selection,
      includeListIds: toggleAudienceId(selection.includeListIds, id),
    });
  const setIncludeSegment = (id: string) =>
    onChange({
      ...selection,
      includeSegmentIds: toggleAudienceId(selection.includeSegmentIds, id),
    });
  const setExcludeList = (id: string) =>
    onChange({
      ...selection,
      excludeListIds: toggleAudienceId(selection.excludeListIds ?? [], id),
    });
  const setExcludeSegment = (id: string) =>
    onChange({
      ...selection,
      excludeSegmentIds: toggleAudienceId(selection.excludeSegmentIds, id),
    });
  const overlapCount = getAudienceOverlapIds(selection).length;

  const handleSegmentSaved = (segmentId: string) => {
    const id = segmentId.trim();
    setNewSegmentOpen(false);
    if (!id) return;

    pendingSegmentIdsRef.current.add(id);
    void refetchSegments();

    const nextInclude = selection.includeSegmentIds.includes(id)
      ? selection.includeSegmentIds
      : [...selection.includeSegmentIds, id];
    onChange({
      ...selection,
      includeSegmentIds: nextInclude,
    });

    toast({
      variant: 'success',
      title: 'Segment saved and selected for this campaign.',
    });
  };

  return (
    <div className="mx-auto flex h-full max-h-full w-full flex-col gap-5 overflow-hidden rounded-lg border border-border bg-card p-4 shadow-sm md:p-6">
      <div className="campaign-scrollbar min-h-0 flex-1 space-y-5 overflow-y-auto rounded-md pr-1">
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Include — lists</p>
          <div className="flex flex-col gap-0.5">
            {isListsLoading ? (
              <p className="text-xs text-muted-foreground">Loading lists…</p>
            ) : isListsError ? (
              <p className="text-xs text-destructive">Failed to load lists.</p>
            ) : selectableLists.length === 0 ? (
              <p className="text-xs text-muted-foreground">None yet.</p>
            ) : (
              selectableLists.map((list) => (
                <CheckRow
                  key={list.id}
                  checked={selection.includeListIds.includes(list.id)}
                  label={list.name.trim() || 'Untitled list'}
                  disabled={(list.memberCount ?? 0) === 0}
                  sub={
                    typeof list.memberCount === 'number'
                      ? list.memberCount === 0
                        ? '0 members — add contacts to use this list'
                        : formatMembers(list.memberCount)
                      : undefined
                  }
                  onToggle={() => setIncludeList(list.id)}
                />
              ))
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Include — segments{' '}
            <button
              type="button"
              className="ml-2 align-middle text-[13px] font-medium text-primary-600 hover:text-primary-700"
              onClick={() => setNewSegmentOpen(true)}
            >
              + New segment
            </button>
          </p>
          <div className="flex flex-col gap-0.5">
            {isSegmentsLoading ? (
              <p className="text-xs text-muted-foreground">Loading segments…</p>
            ) : isSegmentsError ? (
              <p className="text-xs text-destructive">Failed to load segments.</p>
            ) : selectableSegments.length === 0 ? (
              <p className="text-xs text-muted-foreground">None yet.</p>
            ) : (
              selectableSegments.map((segment) => (
                <CheckRow
                  key={segment.itemId}
                  checked={selection.includeSegmentIds.includes(segment.itemId)}
                  label={segment.name.trim() || 'Untitled segment'}
                  sub={
                    typeof segment.matchCount === 'number'
                      ? formatMembers(segment.matchCount)
                      : segment.description?.trim() || undefined
                  }
                  onToggle={() => setIncludeSegment(segment.itemId)}
                />
              ))
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Exclude — lists</p>
          <div className="flex flex-col gap-0.5">
            {selectableLists.length === 0 ? (
              <p className="text-xs text-muted-foreground">None yet.</p>
            ) : (
              selectableLists.map((list) => (
                <CheckRow
                  key={`exclude-${list.id}`}
                  checked={(selection.excludeListIds ?? []).includes(list.id)}
                  label={list.name.trim() || 'Untitled list'}
                  sub={
                    typeof list.memberCount === 'number'
                      ? formatMembers(list.memberCount)
                      : undefined
                  }
                  onToggle={() => setExcludeList(list.id)}
                />
              ))
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Exclude — segments</p>
          <div className="flex flex-col gap-0.5">
            {selectableSegments.length === 0 ? (
              <p className="text-xs text-muted-foreground">None yet.</p>
            ) : (
              selectableSegments.map((segment) => (
                <CheckRow
                  key={`exclude-${segment.itemId}`}
                  checked={selection.excludeSegmentIds.includes(segment.itemId)}
                  label={segment.name.trim() || 'Untitled segment'}
                  sub={
                    typeof segment.matchCount === 'number'
                      ? formatMembers(segment.matchCount)
                      : segment.description?.trim() || undefined
                  }
                  onToggle={() => setExcludeSegment(segment.itemId)}
                />
              ))
            )}
          </div>
        </div>

        {overlapCount > 0 ? (
          <p className="text-xs text-muted-foreground">
            Anything selected in both Include and Exclude is excluded — Exclude takes priority.
          </p>
        ) : null}

        {!selectionValid && !designPreview ? (
          <p className="text-sm text-destructive" role="alert">
            Select at least one list or segment.
          </p>
        ) : null}
        {selectionValid && !isPublishAudienceResolved && !designPreview ? (
          <p className="text-sm text-destructive" role="alert">
            Selected segments could not be resolved. Re-select the audience before continuing.
          </p>
        ) : null}
      </div>

      <div className="flex flex-none flex-col gap-3 rounded-lg border border-border p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiTile label="Audience" value={countsLoading ? '…' : formatCount(audienceCount)} />
          <KpiTile label="Sendable" value={countsLoading ? '…' : formatCount(sendableCount)} />
          <KpiTile label="Skipped" value={countsLoading ? '…' : formatCount(skippedCount)} />
          <KpiTile label="Lists+segments" value={String(listsAndSegmentsCount)} />
        </div>
        {selectionValid && !isKpiError && kpiData?.skips && skippedCount > 0 ? (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {SKIP_REASON_LABELS.map(({ key, label }) => {
              const value = kpiData.skips?.[key] ?? 0;
              return value > 0 ? (
                <li key={key}>
                  {label}: <span className="font-medium text-foreground">{formatCount(value)}</span>
                </li>
              ) : null;
            })}
          </ul>
        ) : null}
        {selectionValid && isKpiError ? (
          <p className="text-sm text-destructive" role="alert">
            Failed to load audience counts
          </p>
        ) : null}
      </div>

      <SegmentBuilderDrawer
        open={newSegmentOpen}
        mode="create"
        segmentId={null}
        onOpenChange={setNewSegmentOpen}
        onSaved={handleSegmentSaved}
      />
    </div>
  );
};
